import './claim-entity.detail.claimant.modal.scss';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Stack,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import { NewFormTextField } from '../../../presentation';
import { PhoneExtField } from '../../../presentation/components/phone-ext-field';
import { NewConvergeButton } from '../../../presentation/components/new-button';
import {
  CreateClaimantField,
  getCreateClaimantFieldProps,
} from '@converge/data';
import {
  useUpsertClaimAdditionalContactMutation,
  ClaimContactType,
} from '@converge/graphql';
import { useConvergeSnackBar } from '../../../contexts';

interface AdditionalInfoFormData {
  firstName: string;
  lastName: string;
  email: string;
  primaryPhoneNumber: string;
  primaryPhoneNumberExtn: string;
}

interface ClaimEntityAdditionalInfoModalProps {
  open: boolean;
  onClose: () => void;
  claimUuid: string;
  TargetUuid: string;
  type: ClaimContactType;
  onSaved?: () => void;
  uuid?: string;
  initialData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
}

const schema = yup.object().shape({
  firstName: yup.string(),
  lastName: yup.string(),
  email: yup.string().email('Invalid email'),
  primaryPhoneNumber: yup.string(),
  primaryPhoneNumberExtn: yup.string(),
});

export const ClaimEntityAdditionalInfoModal: React.FC<
  ClaimEntityAdditionalInfoModalProps
> = ({
  open,
  onClose,
  claimUuid,
  TargetUuid,
  type,
  onSaved,
  uuid,
  initialData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { notify } = useConvergeSnackBar();
  const { upsertClaimAdditionalContact } =
    useUpsertClaimAdditionalContactMutation();

  console.log('arash', TargetUuid, type);

  // Split phone and extension if initial data has phone
  const getPhoneAndExt = (phone?: string) => {
    if (!phone) return { phone: '', ext: '' };
    const parts = phone.split('x');
    return {
      phone: parts[0] || '',
      ext: parts[1] || '',
    };
  };

  const { phone: initialPhone, ext: initialExt } = getPhoneAndExt(
    initialData?.phone
  );

  const formMethods = useForm<AdditionalInfoFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      email: initialData?.email || '',
      primaryPhoneNumber: initialPhone,
      primaryPhoneNumberExtn: initialExt,
    },
    mode: 'onChange',
  });

  const {
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = formMethods;

  const watchedFields = watch();

  // Reset form with initial data when modal opens in edit mode
  useEffect(() => {
    if (open && initialData) {
      const { phone: phoneValue, ext: extValue } = getPhoneAndExt(
        initialData.phone
      );
      reset({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        email: initialData.email || '',
        primaryPhoneNumber: phoneValue,
        primaryPhoneNumberExtn: extValue,
      });
    }
  }, [open, initialData, reset]);

  const isValid = Object.entries(watchedFields).some(([key, value]) => {
    if (key === 'primaryPhoneNumberExtn') {
      return false;
    }
    return typeof value === 'string' && value.trim().length > 0;
  });

  // Check if form has been modified in edit mode
  const hasChanges = () => {
    if (!uuid || !initialData) return true; // In create mode, always allow save if valid

    const { phone: initialPhone, ext: initialExt } = getPhoneAndExt(
      initialData.phone
    );

    return (
      watchedFields.firstName !== (initialData.firstName || '') ||
      watchedFields.lastName !== (initialData.lastName || '') ||
      watchedFields.email !== (initialData.email || '') ||
      watchedFields.primaryPhoneNumber !== initialPhone ||
      watchedFields.primaryPhoneNumberExtn !== initialExt
    );
  };

  const hasEmailError = !!errors.email;

  const handleClose = () => {
    reset({
      firstName: '',
      lastName: '',
      email: '',
      primaryPhoneNumber: '',
      primaryPhoneNumberExtn: '',
    });
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    setIsSubmitting(true);
    try {
      console.log('Submitting additional info:', {
        claimUuid,
        TargetUuid,
        type,
        data,
      });

      // Format phone number with extension
      const formattedPhone = data.primaryPhoneNumberExtn
        ? `${data.primaryPhoneNumber}x${data.primaryPhoneNumberExtn}`
        : data.primaryPhoneNumber;

      // Helper to convert empty strings to null
      const getValueOrNull = (value?: string) => {
        const trimmed = value?.trim();
        return trimmed && trimmed.length > 0 ? trimmed : null;
      };

      // Call mutation with simple object
      // Always send claimUuid and targetUuid
      // Only send uuid when in edit mode
      await upsertClaimAdditionalContact({
        claimUuid,
        targetUuid: TargetUuid,
        type,
        firstName: data.firstName?.trim() || '',
        lastName: data.lastName?.trim() || '',
        emailAddress: getValueOrNull(data.email),
        phoneNumber: getValueOrNull(formattedPhone),
        ...(uuid && { uuid }),
      });

      notify(
        uuid
          ? 'Additional contact successfully updated'
          : 'Additional contact successfully added',
        {
          variant: 'success',
        }
      );

      if (onSaved) {
        onSaved();
      }
      handleClose();
    } catch (error) {
      console.error('Error saving additional info:', error);
      notify('Error saving additional contact', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth={true}
      PaperProps={{
        sx: {
          width: '800px',
          height: 'auto',
          margin: 'auto',
        },
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <DialogTitle sx={{ padding: 4, paddingBottom: 5, marginBottom: 6 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              paddingTop="10px"
              paddingRight="15px"
            >
              <Box className="main-title-claimant-modal" sx={{ marginTop: 2 }}>
                {uuid
                  ? 'Edit Additional Information'
                  : 'Add Additional Information'}
              </Box>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>

          <DialogContent>
            <Box>
              <Stack
                direction="row"
                width="100%"
                spacing={5}
                sx={{ mb: 3 }}
                className="claimant-row-container"
              >
                <Box
                  sx={{
                    width: {
                      xs: '45%',
                      xl: '45.2%',
                    },
                  }}
                >
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.FirstName
                    )}
                    placeholder="Enter First Name"
                    error={!!errors.firstName}
                    helperText={(errors.firstName?.message as string) || ''}
                  />
                </Box>
                <Box
                  sx={{
                    width: {
                      xs: '45%',
                      xl: '45.2%',
                    },
                  }}
                >
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.LastName
                    )}
                    containerWidth={12}
                    placeholder="Enter Last Name"
                    error={!!errors.lastName}
                    helperText={(errors.lastName?.message as string) || ''}
                  />
                </Box>
              </Stack>

              <Stack
                direction="row"
                width="100%"
                spacing={5}
                sx={{ mb: 3 }}
                className="claimant-row-container"
              >
                <Box
                  sx={{
                    width: {
                      xs: '45%',
                      xl: '45.2%',
                    },
                  }}
                >
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(CreateClaimantField.Email)}
                    containerWidth={12}
                    placeholder="name@domain.com"
                    error={!!errors.email}
                    helperText={(errors.email?.message as string) || ''}
                  />
                </Box>
                <Box
                  sx={{
                    width: {
                      xs: '45%',
                      xl: '45.2%',
                    },
                  }}
                >
                  <PhoneExtField
                    isContactpage={true}
                    dataTestId="additional-info-primaryPhone"
                    placeholder="415-555-1212"
                    className="phone-field"
                    mainClassName="primary-phone2"
                    autoComplete={false}
                    dataExtTestId="mobile-phone-extn"
                    classNameExt="mobile-phone-extn"
                    fieldPropsIdx={CreateClaimantField.PrimaryPhoneNumber}
                    fieldPropsExtIdx={
                      CreateClaimantField.PrimaryPhoneNumberExtn
                    }
                  />
                </Box>
              </Stack>
            </Box>
          </DialogContent>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mr: '35px',
              mb: '20px',
            }}
          >
            <NewConvergeButton
              variant="outlined"
              label="CANCEL"
              onClick={handleClose}
              disabled={isSubmitting}
            />
            <Box style={{ width: '98px' }}>
              <NewConvergeButton
                type="submit"
                disabled={
                  isSubmitting || !isValid || hasEmailError || !hasChanges()
                }
                label="Save"
              />
            </Box>
          </Box>
        </form>
      </FormProvider>
    </Dialog>
  );
};
